import React, { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";

import { z } from "zod";
import { useForm } from "react-hook-form";
import { memberSchema } from "@/utils/schemaHelper";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/uiComponents/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/uiComponents/components/ui/form";
import { Input } from "@/uiComponents/components/ui/input";

import { TiTick } from "react-icons/ti";

import { useDispatch, useSelector } from "react-redux";

import { ChevronDown } from "lucide-react";
import { RootState } from "@/store/store";
import { setLoading } from "@/store/redux/appSlice";
import {
  getBranchApi,
  getMembersApi,
  inviteMemberApi,
} from "@/modules/organizationServices";

import { MemberErrors } from "@/enums/errorMessages";

import { addMemberProps } from "@/types/organization";
import { ErrorHelper } from "@/utils/errorHelper";
import { setBranches } from "@/store/redux/organization/branchSlice";
import { toastMessage } from "@/utils/toastHelper";
import { roleLabels, roles } from "@/enums/roles";
import { ReusableButton } from "@/reusable/reusableButton";
import {
  setInvitations,
  setMembers,
} from "@/store/redux/organization/memberSlice";

const AddMembers: React.FC<addMemberProps> = ({ onClose, isOpen }) => {
  if (!isOpen) return null;
  const dispatch = useDispatch();
  const role = useSelector((state: RootState) => state.userDetail.role);
  const loading = useSelector((state: RootState) => state.app.loading);
  const userDetails = useSelector(
    (state: RootState) => state.userDetail.userDetail,
  );
  const branches = useSelector((state: RootState) => state.branch.branches);
  const orgId = userDetails?.organizationId;
  const roleDropRef = useRef<HTMLDivElement>(null);
  const branchDropRef = useRef<HTMLDivElement>(null);
  const [isBranchOpen, setIsBranchOpen] = useState<boolean>(false);
  const [isRoleOpen, setIsRoleOpen] = useState<boolean>(false);
  const form = useForm<z.infer<typeof memberSchema>>({
    resolver: zodResolver(memberSchema),
    mode: "onChange",
    defaultValues: {
      email: "",
      role: "",
      organization: orgId,
      branch: null,
    },
  });
  const getMembers = async () => {
    try {
      const res = await getMembersApi();
      const userResponse = res.users;
      const invitationResponse = res.invitations;

      if (userResponse != undefined) {
        dispatch(setMembers(userResponse));
      }
      if (invitationResponse != undefined) {
        dispatch(setInvitations(invitationResponse));
      }
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    }
  };

  const getBranches = async () => {
    dispatch(setLoading(true));
    try {
      if (!orgId) return;
      const res = await getBranchApi(orgId);
      const response = res.branches;
      if (response !== undefined) {
        const branches = Array.isArray(response) ? response : [response];
        dispatch(setBranches(branches));
      }
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    getBranches();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        branchDropRef.current &&
        !branchDropRef.current.contains(e.target as Node)
      ) {
        setIsBranchOpen(false);
      }
      if (
        roleDropRef.current &&
        !roleDropRef.current.contains(e.target as Node)
      ) {
        setIsRoleOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const onSubmit = async (values: z.infer<typeof memberSchema>) => {
    if (loading) return;
    dispatch(setLoading(true));

    try {
      await inviteMemberApi(values, role);

      onClose();
      getMembers();

      getMembers();
      toastMessage("success", MemberErrors.MEMBER_INVITED);
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Members</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 ">
            <div className="min-h-6 mb-1"></div>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="max-h-13 ">
                  <FormLabel>Email</FormLabel>
                  <div className="relative">
                    <FormControl>
                      <Input
                        {...field}
                        className={`bg-white ${
                          field.value && !form.formState.errors.email
                            ? "border-green-500 transition-all focus-visible:ring-green-200  "
                            : ""
                        }`}
                      />
                    </FormControl>
                    <div className="absolute right-2 top-2">
                      {field.value && !form.formState.errors.email && (
                        <TiTick className="text-green-700" />
                      )}
                    </div>
                  </div>

                  <div className="min-h-6">
                    <FormMessage className="text-xs" />
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem className="max-h-13">
                  <FormLabel>Role</FormLabel>
                  <div className="relative">
                    <FormControl>
                      <div className="relative" ref={roleDropRef}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsRoleOpen(!isRoleOpen);
                          }}
                          className="w-full border p-2 rounded-md text-left flex items-center bg-white justify-between"
                        >
                          {field.value === roles.ORG_ADMIN
                            ? roleLabels.ORG_ADMIN
                            : field.value === roles.MEMBER
                              ? roleLabels.MEMBER
                              : "Select Role"}
                          <ChevronDown
                            size={16}
                            className={`transition-transform duration-200 ${
                              isRoleOpen ? "rotate-180" : ""
                            }`}
                          />
                        </button>

                        {isRoleOpen && (
                          <div className="absolute z-10  w-full bg-white border rounded-md shadow-lg">
                            <div
                              className={`p-2 hover:bg-gray-100 cursor-pointer ${field.value == roles.ORG_ADMIN ? "bg-gray-300" : ""}`}
                              onClick={() => {
                                field.onChange(roles.ORG_ADMIN);
                                setIsRoleOpen(false);
                              }}
                            >
                              {roleLabels.ORG_ADMIN}
                            </div>
                            <div
                              className={`p-2 hover:bg-gray-100 cursor-pointer ${field.value == roles.MEMBER ? "bg-gray-300" : ""}`}
                              onClick={() => {
                                field.onChange(roles.MEMBER);
                                setIsRoleOpen(false);
                              }}
                            >
                              {roleLabels.MEMBER}
                            </div>
                          </div>
                        )}
                      </div>
                    </FormControl>
                  </div>

                  <div className="min-h-6">
                    <FormMessage className="text-xs" />
                  </div>
                </FormItem>
              )}
            />
            {form.watch("role") == "member" && (
              <FormField
                name="branch"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Branch</FormLabel>
                    <FormControl>
                      <div className="relative" ref={branchDropRef}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsBranchOpen(!isBranchOpen);
                          }}
                          className="w-full border p-2 rounded-md bg-white text-left flex items-center justify-between"
                        >
                          {field.value
                            ? branches.find((b) => b.id === field.value)
                                ?.branchName
                            : "Select a branch"}
                          <ChevronDown
                            size={16}
                            className={`transition-transform duration-200 ${
                              isBranchOpen ? "rotate-180" : ""
                            }`}
                          />
                        </button>

                        {isBranchOpen && (
                          <ul className="absolute left-0  w-full bg-white border rounded-md shadow z-50 max-h-60 overflow-y-auto">
                            {branches.map((branch) => (
                              <li
                                key={branch.id}
                                onClick={() => {
                                  field.onChange(branch.id);
                                  setIsBranchOpen(false);
                                }}
                                className={`p-2 hover:bg-gray-100 cursor-pointer ${field.value == branch.id ? "bg-gray-300" : ""}`}
                              >
                                {branch.branchName}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="organization"
              render={({ field }) => (
                <FormItem className="max-h-13 ">
                  <FormLabel hidden>Organization</FormLabel>

                  <FormControl>
                    <Input hidden {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex justify-center mt-4 mr-2">
              <ReusableButton
                label="Add"
                className=" mt-5 mb-2 "
                loading={loading}
              />
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
export default AddMembers;
